import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Stack,
  Chip,
  CircularProgress,
  Alert,
  IconButton,
  LinearProgress,
} from "@mui/material";
import ArrowBack from "@mui/icons-material/ArrowBack";
import CheckCircle from "@mui/icons-material/CheckCircle";
import Cancel from "@mui/icons-material/Cancel";
import Replay from "@mui/icons-material/Replay";

import { getModuleQuiz, submitQuiz } from "./englishApi";

const PRIMARY = "#006D77";
const SECONDARY = "#00A8B5";
const BORDER = "#E0F2F1";

export default function EnglishQuizPage() {
  const { moduleId } = useParams();
  const navigate = useNavigate();

  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [index, setIndex] = useState(0);
  const [selected, setSelected] = useState(null);
  const [feedback, setFeedback] = useState(null); // { correct, correct_index, explanation }
  const [submitting, setSubmitting] = useState(false);
  const [score, setScore] = useState(0);
  const [finished, setFinished] = useState(false);

  useEffect(() => {
    getModuleQuiz(moduleId)
      .then((data) => setQuestions(data.questions || []))
      .catch(() => setError("Couldn't load the quiz. Please try again."))
      .finally(() => setLoading(false));
  }, [moduleId]);

  const currentQuestion = questions[index];

  const handleSelect = async (optionIndex) => {
    if (feedback) return; // already answered this question
    setSelected(optionIndex);
    setSubmitting(true);
    try {
      const result = await submitQuiz(moduleId, { [currentQuestion.id]: optionIndex });
      const r = result.results[0];
      setFeedback(r);
      if (r.correct) setScore((s) => s + 1);
    } catch {
      setError("Couldn't check that answer. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleNext = () => {
    if (index + 1 >= questions.length) {
      setFinished(true);
      return;
    }
    setIndex((i) => i + 1);
    setSelected(null);
    setFeedback(null);
  };

  const handleRestart = () => {
    setIndex(0);
    setSelected(null);
    setFeedback(null);
    setScore(0);
    setFinished(false);
  };
const DRAWER_WIDTH = 280;
if (loading) {
  return (
    <Box sx={{ display: "flex", justifyContent: "center", p: 8, ml: { xs: 0, md: `${DRAWER_WIDTH}px` } }}>
      <CircularProgress sx={{ color: PRIMARY }} />
    </Box>
  );
}

// error
if (error) {
  return (
    <Box sx={{ p: 3, ml: { xs: 0, md: `${DRAWER_WIDTH}px` } }}>
      <Alert severity="error">{error}</Alert>
    </Box>
  );
}

  if (!questions.length) {
    return (
       <Box sx={{ p: 3, maxWidth: 600, mx: "auto", textAlign: "center", ml: { xs: 0, md: `${DRAWER_WIDTH}px` } }}>
        <IconButton onClick={() => navigate(`/english/${moduleId}`)} sx={{ alignSelf: "flex-start" }}>
          <ArrowBack />
        </IconButton>
        <Typography variant="h6" fontWeight={700} sx={{ mt: 2 }}>
          No quiz available yet for this module.
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Check back soon, or continue with the tutorials for now.
        </Typography>
      </Box>
    );
  }

  if (finished) {
    const pct = Math.round((score / questions.length) * 100);
    return (
      <Box sx={{ p: 3, maxWidth: 560, mx: "auto", ml: { xs: 0, md: `${DRAWER_WIDTH}px` } }}>
        <Card sx={{ borderRadius: 3, border: `1px solid ${BORDER}`, boxShadow: "none", textAlign: "center", p: 3 }}>
          <Typography variant="h5" fontWeight={700} sx={{ mb: 1 }}>
            Practice complete!
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            This was just for practice — nothing was saved.
          </Typography>
          <Typography variant="h3" fontWeight={800} sx={{ color: PRIMARY, mb: 1 }}>
            {score} / {questions.length}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            {pct}% correct
          </Typography>
          <Stack direction="row" spacing={2} justifyContent="center">
            <Button variant="outlined" startIcon={<Replay />} onClick={handleRestart}>
              Try Again
            </Button>
            <Button
              variant="contained"
              onClick={() => navigate(`/english/${moduleId}`)}
              sx={{ background: "linear-gradient(135deg, #006D77 0%, #00A8B5 100%)" }}
            >
              Back to Module
            </Button>
          </Stack>
        </Card>
      </Box>
    );
  }

  const progressPct = Math.round((index / questions.length) * 100);

  return (
    <Box sx={{ bgcolor: "#F4F6F8", minHeight: "100vh", p: { xs: 2, md: 3 }, ml: { xs: 0, md: `${DRAWER_WIDTH}px` } }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2, maxWidth: 640, mx: "auto" }}>
        <IconButton onClick={() => navigate(`/english/${moduleId}`)} size="small">
          <ArrowBack />
        </IconButton>
        <Box sx={{ flex: 1 }}>
          <Typography variant="subtitle1" fontWeight={700}>
            Practice Quiz
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Question {index + 1} of {questions.length}
          </Typography>
        </Box>
        <Chip label={`Score: ${score}`} sx={{ bgcolor: "#E0F2F1", color: PRIMARY, fontWeight: 700 }} />
      </Stack>

      <Box sx={{ maxWidth: 640, mx: "auto", mb: 2 }}>
        <LinearProgress
          variant="determinate"
          value={progressPct}
          sx={{ height: 6, borderRadius: 5, bgcolor: "#D9EEEE", "& .MuiLinearProgress-bar": { bgcolor: SECONDARY, borderRadius: 5 } }}
        />
      </Box>

      <Card sx={{ maxWidth: 640, mx: "auto", borderRadius: 3, border: `1px solid ${BORDER}`, boxShadow: "none" }}>
        <CardContent sx={{ p: 3 }}>
          {currentQuestion.category && (
            <Chip label={currentQuestion.category} size="small" variant="outlined" sx={{ mb: 2 }} />
          )}
          <Typography variant="h6" fontWeight={700} sx={{ mb: 3 }}>
            {currentQuestion.question}
          </Typography>

          <Stack spacing={1.5}>
            {currentQuestion.options.map((option, i) => {
              const isSelected = selected === i;
              const isCorrectOption = feedback && i === feedback.correct_index;
              const isWrongSelected = feedback && isSelected && !feedback.correct;

              let borderColor = BORDER;
              let bgColor = "white";
              if (feedback) {
                if (isCorrectOption) {
                  borderColor = "#43A047";
                  bgColor = "#E8F5E9";
                } else if (isWrongSelected) {
                  borderColor = "#E53935";
                  bgColor = "#FDECEA";
                }
              } else if (isSelected) {
                borderColor = PRIMARY;
                bgColor = "#F8FFFE";
              }

              return (
                <Button
                  key={i}
                  fullWidth
                  disabled={submitting || !!feedback}
                  onClick={() => handleSelect(i)}
                  sx={{
                    justifyContent: "space-between",
                    textAlign: "left",
                    textTransform: "none",
                    px: 2,
                    py: 1.3,
                    borderRadius: 2,
                    border: `1.5px solid ${borderColor}`,
                    bgcolor: bgColor,
                    color: "text.primary",
                    "&:hover": { bgcolor: feedback ? bgColor : "#F8FFFE" },
                    "&.Mui-disabled": { color: "text.primary" },
                  }}
                  endIcon={
                    feedback && isCorrectOption ? (
                      <CheckCircle sx={{ color: "#2E7D32" }} />
                    ) : feedback && isWrongSelected ? (
                      <Cancel sx={{ color: "#E53935" }} />
                    ) : null
                  }
                >
                  {option}
                </Button>
              );
            })}
          </Stack>

          {feedback && (
            <Box
              sx={{
                mt: 3,
                p: 2,
                borderRadius: 2,
                bgcolor: feedback.correct ? "#E8F5E9" : "#FDECEA",
                border: `1px solid ${feedback.correct ? "#43A047" : "#E53935"}`,
              }}
            >
              <Typography variant="subtitle2" fontWeight={700} sx={{ color: feedback.correct ? "#2E7D32" : "#C62828", mb: 0.5 }}>
                {feedback.correct ? "Correct!" : "Not quite."}
              </Typography>
              {feedback.explanation && (
                <Typography variant="body2" color="text.secondary">
                  {feedback.explanation}
                </Typography>
              )}
            </Box>
          )}

          <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 3 }}>
            <Button
              variant="contained"
              disabled={!feedback}
              onClick={handleNext}
              sx={{ background: "linear-gradient(135deg, #006D77 0%, #00A8B5 100%)" }}
            >
              {index + 1 >= questions.length ? "Finish" : "Next Question"}
            </Button>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
}
